/** Siting a guild building: `BuildGuildBuildingWindow`'s catalogue, cost and
 *  view, positioning and preview modes (`root/uiguild.py:2002-2627`). */
import GUILD_BUILDING_ROWS from "./guildbuildings.json";
import { FOV_DEG } from "../render/view";
import { HEADING_STEPS, MM_PER_LEGACY_UNIT, mmToLegacy } from "../sim/fixed";

/** One `BUILDING_DATA_LIST` row, as `tools/import_guild_buildings.py` writes
 *  it out of `guildbuildinglist.txt` (`root/playersettingmodule.py:1343-1357`). */
export interface GuildBuildingRow {
  readonly vnum: number;
  readonly type: string;
  readonly name: string;
  readonly rotLimit: readonly [number, number, number];
  readonly price: number;
  /** Stone, log and plywood, in `MATERIAL_*_INDEX` order (`root/uiguild.py:37-39`). */
  readonly materials: readonly [number, number, number];
  readonly show: boolean;
  /** `lRegion`, the prototype's own footprint rectangle in legacy units
   *  (`game/src/building.cpp:52-56`); null for a row with no matching
   *  prototype, none of which `buildingsOfCategory` ever offers. */
  readonly region: readonly [number, number, number, number] | null;
}

export const GUILD_BUILDINGS = GUILD_BUILDING_ROWS as unknown as readonly GuildBuildingRow[];

/** `GUILD_CATEGORY_LIST` on the European branch (`root/uiguild.py:2017-2021`),
 *  in `locale_game.txt`'s words. BUILDIN and WALL rows are in no category. */
export const GUILD_BUILDING_CATEGORIES: readonly (readonly [type: string, label: string])[] = [
  ["HEADQUARTER", "Main Building"], // locale_game.txt:176
  ["FACILITY", "Outbuilding"], // locale_game.txt:174
  ["OBJECT", "Objects"], // locale_game.txt:184
];

export const MATERIAL_STONE_ID = 90010; // root/uiguild.py:41
export const MATERIAL_LOG_ID = 90011; // root/uiguild.py:42
export const MATERIAL_PLYWOOD_ID = 90012; // root/uiguild.py:43

/** `BUILDING_ALPHA` (`root/uiguild.py:2040`): the ghost outside preview. */
export const BUILDING_ALPHA = 0.55;
/** `START_INSTANCE_INDEX` (`root/uiguild.py:2045`): the ghost's instance id. */
export const SITING_INSTANCE_ID = 123450;
/** `ENABLE_COLOR` (`root/uiguild.py:2042`), packed the way `D3DXCOLOR`'s
 *  `DWORD` conversion rounds `0.7607 * 255`. */
export const ENABLE_COLOR = "rgb(194, 194, 194)";
/** `DISABLE_COLOR` (`root/uiguild.py:2043`): `(0.9, 0.4745, 0.4627)` packed. */
export const DISABLE_COLOR = "rgb(230, 121, 118)";

/** `app.SetCameraSetting(x, -y, z, 3000, 0, 30)` (`root/uiguild.py:2305,2552`):
 *  zoom in centimetres, rotation and pitch in degrees. */
export const SITING_CAMERA = { distanceMm: 3000 * 10, rotationDeg: 0, pitchDeg: 30 } as const;

export const GUILD_NOT_ENOUGH_MONEY = "Not enough Yang for this building."; // locale_game.txt:182
export const GUILD_NOT_ENOUGH_MATERIAL = "Not enough building material for this building."; // locale_game.txt:181

/** The rows `__RefreshItem` lists for one category: that type, `SHOW` set
 *  (`root/uiguild.py:2567-2574`). */
export function buildingsOfCategory(type: string): GuildBuildingRow[] {
  return GUILD_BUILDINGS.filter((row) => row.type === type && row.show);
}

/** What `__SetBuildingData` prints and colours against the bag
 *  (`root/uiguild.py:2457-2491`): enough when the held amount is not below
 *  the need (`__IsEnoughMoney` and the three material checks, `:2413-2451`). */
export interface BuildingCost {
  readonly price: number;
  readonly stone: number;
  readonly log: number;
  readonly plywood: number;
  readonly enoughMoney: boolean;
  readonly enoughStone: boolean;
  readonly enoughLog: boolean;
  readonly enoughPlywood: boolean;
}

export function buildingCost(
  row: GuildBuildingRow,
  gold: number,
  countOf: (vnum: number) => number,
): BuildingCost {
  const [stone, log, plywood] = row.materials;
  return {
    price: row.price,
    stone,
    log,
    plywood,
    enoughMoney: !(gold < row.price),
    enoughStone: !(countOf(MATERIAL_STONE_ID) < stone),
    enoughLog: !(countOf(MATERIAL_LOG_ID) < log),
    enoughPlywood: !(countOf(MATERIAL_PLYWOOD_ID) < plywood),
  };
}

/** `Build`'s refusals, money before material (`root/uiguild.py:2275-2288`),
 *  or null when it sends. */
export function buildRefusal(cost: BuildingCost): string | null {
  if (!cost.enoughMoney) return GUILD_NOT_ENOUGH_MONEY;
  if (!cost.enoughStone || !cost.enoughLog || !cost.enoughPlywood) return GUILD_NOT_ENOUGH_MATERIAL;
  return null;
}

/** `__OnChangeRotation`'s Z (`root/uiguild.py:2545`) from a slider at `pos`. */
export function rotationZ(pos: number): number {
  return Math.trunc((pos * 360 + 180) % 360);
}

/** `MODE_VIEW`, `MODE_POSITIONING`, `MODE_PREVIEW` (`root/uiguild.py:2036-2038`). */
export type SitingMode = "view" | "positioning" | "preview";

/** One own-guild buildable rectangle in legacy units, inclusive on every
 *  edge as `GetGuildAreaID` compares (`PythonMiniMap.cpp:744-760`). */
export interface SitingArea {
  readonly left: number;
  readonly top: number;
  readonly right: number;
  readonly bottom: number;
}

/** The translucent instance a sited building draws before it exists. */
export interface SitingGhost {
  readonly vnum: number;
  readonly xMm: number;
  readonly yMm: number;
  readonly heading: number;
  readonly alpha: number;
}

/** A footprint rectangle in world millimetres, the shape `stampObjectRect`
 *  (`sim/collision.ts`) already takes for a durable building's own. */
export interface FootprintMm {
  readonly x0Mm: number;
  readonly y0Mm: number;
  readonly x1Mm: number;
  readonly y1Mm: number;
}

/** The rotated corners of a footprint at one legacy-unit position, exactly
 *  `RequestCreateObject`'s own rotation (`game/src/building.cpp:527-538`),
 *  which is what `ForAttrRegion` then stamps as `ATTR_OBJECT` (`:549`). */
export function footprintMm(
  region: readonly [number, number, number, number],
  xLegacy: number,
  yLegacy: number,
  rotationDeg: number,
): FootprintMm {
  const rad = (rotationDeg * Math.PI) / 180;
  const c = Math.cos(rad);
  const s = Math.sin(rad);
  const [a, b, d, e] = region;
  const corners: readonly [number, number][] = [
    [a, b],
    [a, e],
    [d, b],
    [d, e],
  ];
  const xs = corners.map(([px, py]) => xLegacy + px * c + py * s);
  const ys = corners.map(([px, py]) => yLegacy - px * s + py * c);
  return {
    x0Mm: Math.floor(Math.min(...xs)) * MM_PER_LEGACY_UNIT,
    y0Mm: Math.floor(Math.min(...ys)) * MM_PER_LEGACY_UNIT,
    x1Mm: Math.ceil(Math.max(...xs)) * MM_PER_LEGACY_UNIT,
    y1Mm: Math.ceil(Math.max(...ys)) * MM_PER_LEGACY_UNIT,
  };
}

/** The window's state between `Open` and `Close`: which building, where, at
 *  which slider, in which mode, and where the camera is held. */
export class GuildSiting {
  private opened = false;
  private modeNow: SitingMode = "view";
  private xMm = 0;
  private yMm = 0;
  private lock: { xMm: number; yMm: number } | null = null;
  private lockCount = 0;
  private row: GuildBuildingRow | null = null;
  private sliderZ = 0.5;

  get isOpen(): boolean {
    return this.opened;
  }

  get mode(): SitingMode {
    return this.modeNow;
  }

  get building(): GuildBuildingRow | null {
    return this.row;
  }

  get sliderPos(): number {
    return this.sliderZ;
  }

  /** How many times the event camera has been set: `Open` and every
   *  `__LockCameraMoving` re-apply its zoom, rotation and pitch. */
  get cameraLocks(): number {
    return this.lockCount;
  }

  /** `Open` (`root/uiguild.py:2303-2321`): a fresh window at the character's
   *  feet, in view mode, the camera held there. */
  open(xMm: number, yMm: number): void {
    this.opened = true;
    this.modeNow = "view";
    this.xMm = xMm;
    this.yMm = yMm;
    this.holdCamera();
  }

  /** `Close` (`root/uiguild.py:2323-2334`): the ghost goes, the camera is the
   *  player's again. */
  close(): void {
    this.opened = false;
    this.modeNow = "view";
    this.lock = null;
    this.row = null;
  }

  /** `__OnSelectBuilding` then `__CreateInstance`, which re-centres every
   *  slider (`root/uiguild.py:2248-2274,2508-2535`). Selected by vnum: the
   *  original counted the category's hidden rows into the list index, so a
   *  row after a hidden one built its neighbour. */
  select(vnum: number): void {
    this.row = GUILD_BUILDINGS.find((row) => row.vnum === vnum) ?? null;
    this.sliderZ = 0.5;
  }

  setSlider(pos: number): void {
    this.sliderZ = Math.min(1, Math.max(0, pos));
  }

  /** `__OnSelectPositioningMode` (`root/uiguild.py:2384-2390`): refused in
   *  preview; otherwise the window hides and the ghost follows the pick. */
  beginPositioning(): boolean {
    if (!this.opened || this.modeNow === "preview") return false;
    this.modeNow = "positioning";
    return true;
  }

  /** `__OnEnterPreviewMode` (`root/uiguild.py:2392-2401`): refused while
   *  positioning; the camera is let go and the ghost is drawn solid. */
  enterPreview(): boolean {
    if (!this.opened || this.modeNow === "positioning") return false;
    this.modeNow = "preview";
    this.lock = null;
    return true;
  }

  /** `__OnLeavePreviewMode` through `__RestoreViewMode` (`root/uiguild.py:2403-2411`). */
  leavePreview(): void {
    if (!this.opened) return;
    this.restoreView();
  }

  /** `BUILD_OnUpdate` (`root/interfacemodule.py:1603-1610`): while positioning
   *  the ghost stands on the picked ground. */
  pointAt(xMm: number, yMm: number): void {
    if (!this.opened || this.modeNow !== "positioning") return;
    this.xMm = xMm;
    this.yMm = yMm;
  }

  /** `BUILD_OnMouseLeftButtonDown` (`root/interfacemodule.py:1614-1627`): true
   *  when the world must not see the click. Positioning settles on a point
   *  inside the guild's own ground (`SettleCurrentPosition`,
   *  `root/uiguild.py:2587-2599`); preview lets the player walk. */
  click(ownAreas: readonly SitingArea[]): boolean {
    if (!this.opened) return false;
    if (this.modeNow === "preview") return false;
    if (this.modeNow === "positioning") {
      const x = Math.trunc(mmToLegacy(this.xMm));
      const y = Math.trunc(mmToLegacy(this.yMm));
      const inside = ownAreas.some(
        (area) => x >= area.left && y >= area.top && x <= area.right && y <= area.bottom,
      );
      if (inside) this.restoreView();
    }
    return true;
  }

  /** Where the event camera stands, or null when the player's own camera runs
   *  (closed, or preview's `SetDefaultCamera`). */
  get cameraAnchor(): { readonly xMm: number; readonly yMm: number } | null {
    return this.opened ? this.lock : null;
  }

  /** `SetBuildingPosition`'s readout (`root/uiguild.py:2601-2605`), in the
   *  legacy units `/build` and `GuildPlaceBuilding` carry. */
  get position(): { readonly x: number; readonly y: number } {
    return { x: Math.trunc(mmToLegacy(this.xMm)), y: Math.trunc(mmToLegacy(this.yMm)) };
  }

  /** The instance `__CreateInstance` raises (`root/uiguild.py:2248-2274`):
   *  `BUILDING_ALPHA` while the PC blocker is off, solid in preview
   *  (`__EnablePCBlocker`, `:2370-2375`). */
  get ghost(): SitingGhost | null {
    if (!this.opened || this.row === null) return null;
    return {
      vnum: this.row.vnum,
      xMm: this.xMm,
      yMm: this.yMm,
      heading: Math.round((rotationZ(this.sliderZ) * HEADING_STEPS) / 360) % HEADING_STEPS,
      alpha: this.modeNow === "preview" ? 1 : BUILDING_ALPHA,
    };
  }

  /** The ghost's own blocking footprint while `__EnablePCBlocker` would hold
   *  (`root/uiguild.py:2370-2375`): only in preview, and only for a row whose
   *  prototype carries a region. View and positioning draw the same ghost
   *  translucent and let the player walk through it, matching
   *  `__DisablePCBlocker`. */
  get previewFootprintMm(): FootprintMm | null {
    if (this.modeNow !== "preview" || this.row === null || this.row.region === null) return null;
    return footprintMm(this.row.region, this.position.x, this.position.y, rotationZ(this.sliderZ));
  }

  /** What Accept sends for a non-BUILDIN row (`root/uiguild.py:2299`):
   *  `GuildPlaceBuilding` carries the Z rotation only, and every shipped row
   *  has X and Y limits of 0, so `rot_x` and `rot_y` are always 0. */
  get placement(): { vnum: number; x: number; y: number; rotation: number } | null {
    if (!this.opened || this.row === null) return null;
    const { x, y } = this.position;
    return { vnum: this.row.vnum, x, y, rotation: rotationZ(this.sliderZ) };
  }

  private restoreView(): void {
    this.modeNow = "view";
    this.holdCamera();
  }

  private holdCamera(): void {
    this.lock = { xMm: this.xMm, yMm: this.yMm };
    this.lockCount += 1;
  }
}


/** The player's camera the event camera stands in for. */
export interface SitingCamera {
  pitchDeg: number;
  yawDeg: number;
  mmPerPixel: number;
  readonly viewportHeight: number;
  clampZoom(mmPerPixel: number): number;
}

/** What `SetEventCamera` saved and `SetDefaultCamera` puts back, and how many
 *  holds have been applied. */
export interface SitingCameraMemory {
  saved: { pitchDeg: number; yawDeg: number; mmPerPixel: number } | null;
  applied: number;
}

/** Step the camera to the window's hold once a frame. Each new hold sets the
 *  event camera's zoom, rotation and pitch, saving the player's own setting
 *  only when the player's camera was current (`SetEventCamera`,
 *  `PythonApplicationCamera.cpp:264-281`); letting go restores it
 *  (`SetDefaultCamera`, `:295-312`). */
export function stepSitingCamera(siting: GuildSiting, camera: SitingCamera, memory: SitingCameraMemory): void {
  if (siting.cameraAnchor === null) {
    if (memory.saved !== null) {
      camera.pitchDeg = memory.saved.pitchDeg;
      camera.yawDeg = memory.saved.yawDeg;
      camera.mmPerPixel = memory.saved.mmPerPixel;
      memory.saved = null;
    }
    memory.applied = siting.cameraLocks;
    return;
  }
  if (memory.applied === siting.cameraLocks) return;
  memory.saved ??= { pitchDeg: camera.pitchDeg, yawDeg: camera.yawDeg, mmPerPixel: camera.mmPerPixel };
  camera.pitchDeg = SITING_CAMERA.pitchDeg;
  camera.yawDeg = SITING_CAMERA.rotationDeg;
  const spanMm = SITING_CAMERA.distanceMm * 2 * Math.tan((FOV_DEG * Math.PI) / 360);
  camera.mmPerPixel = camera.clampZoom(spanMm / Math.max(1, camera.viewportHeight));
  memory.applied = siting.cameraLocks;
}
